import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  path: string;
  image?: string;
  type?: string;
  schema?: any; // JSON-LD Schema.org object
  keywords?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  noindex?: boolean;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  path,
  image = 'https://ampirivieranayarit.com/wp-content/uploads/2025/03/mob-slide-1.jpg',
  type = 'website',
  schema,
  keywords,
  author = 'AMPI Riviera Nayarit',
  publishedTime,
  modifiedTime,
  noindex = false
}) => {
  const baseUrl = 'https://ampirivieranayarit.com';
  const fullUrl = `${baseUrl}${path}`;
  const fullTitle = `${title} | AMPI Riviera Nayarit`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />

      {/* Keywords */}
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Canonical Link */}
      <link rel="canonical" href={fullUrl} />

      {/* Robots */}
      <meta name="robots" content={noindex ? "noindex,nofollow" : "index, follow"} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="es_MX" />
      <meta property="og:site_name" content="AMPI Riviera Nayarit" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Article specific meta tags */}
      {publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}

      {/* Additional SEO */}
      <meta name="language" content="Spanish" />
      <meta name="author" content={author} />

      {/* Geo tags para local SEO - Riviera Nayarit */}
      <meta name="geo.region" content="MX-NAY" />
      <meta name="geo.placename" content="Riviera Nayarit" />
      <meta name="geo.position" content="20.940764;-105.250894" />
      <meta name="ICBM" content="20.940764, -105.250894" />

      {/* Schema.org JSON-LD */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
